import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// material-ui
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  OutlinedInput,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
// assets
import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

import LogoIcon from 'assets/images/logo_title.png';
// third party
import * as Yup from 'yup';
import { Formik } from 'formik';
// project import
import useAuth from 'hooks/useAuth';
import useSnackbar from 'hooks/useSnackbar';
import ForgotPasswordModal from './ForgotPasswordModal';
import { getAnonIntercomStatus, subscribeAnonIntercomStatus } from 'utils/intercomStatus';

// SUPPORT_URL backs the static "Help & support" fallback bubble, shown ONLY when
// anonymous Intercom live chat isn't actually up (Intercom unconfigured,
// unreachable, or the widget failed to load). When IntercomMessenger boots the
// real launcher this fallback stays hidden, so the two never overlap — and
// because we key off the real boot OUTCOME rather than the server's "configured"
// flag, the user is never left with no help at all. DEFAULT_RENEW_URL is where an
// expired/inactive member is sent to renew; it's overridden by the auth response
// once the backend returns the matching membership site.
const SUPPORT_URL = 'https://www.kloow.com';
const DEFAULT_RENEW_URL = 'https://www.kloow.com';

// Backstop: if the anonymous Intercom boot is still "pending" after this long,
// reveal the static fallback anyway so the login screen always keeps a help
// affordance.
const SUPPORT_FALLBACK_GRACE_MS = 5000;

function openExternal(url) {
  if (typeof url === 'string' && url.trim()) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

// The login dev note calls for three DISTINCT error states, never one generic
// message: (1) wrong credentials, (2) membership inactive/expired (with a renew
// link to the matching site), (3) can't reach the server. We classify off the
// server's error code first and fall back to message/network signals.
function classifyLoginError(response) {
  const code = String(response?.code || '').toUpperCase();
  const message = String(response?.message || '').toLowerCase();

  const rateLimited =
    code.includes('RATE') ||
    code.includes('TOO_MANY') ||
    code === '429' ||
    message.includes('too many') ||
    message.includes('try again in');
  if (rateLimited) return 'rate_limit';

  const connection =
    (typeof navigator !== 'undefined' && navigator.onLine === false) ||
    code.includes('NETWORK') ||
    code.includes('BRIDGE') ||
    code.includes('TIMEOUT') ||
    code === 'ECONNABORTED' ||
    code === 'ETIMEDOUT' ||
    message.includes('reach') ||
    message.includes('network') ||
    message.includes('timed out') ||
    message.includes('connection');
  if (connection) return 'connection';

  const membership =
    code.includes('MEMBERSHIP') ||
    code.includes('EXPIRED') ||
    code.includes('INACTIVE') ||
    code.includes('SUBSCRIPTION') ||
    message.includes('membership') ||
    message.includes('expired') ||
    message.includes('inactive') ||
    message.includes('subscription');
  if (membership) return 'membership';

  return 'credentials';
}

const ERROR_COPY = {
  credentials: 'Incorrect username or password.',
  connection: "Can't reach the server. Check your connection and try again.",
  membership: 'Your membership is inactive or expired.',
  rate_limit: 'Too many attempts. Please wait a moment and try again.'
};

const Login = () => {
  const navigate = useNavigate();
  const { login, forgotPassword } = useAuth();
  const { successMessage } = useSnackbar();

  const [capsWarning, setCapsWarning] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // The static "Help & support" bubble is the fallback for when anonymous
  // Intercom live chat isn't up. We mirror IntercomMessenger's real boot outcome
  // (see utils/intercomStatus): show the bubble only when the launcher is
  // "unavailable". While the boot is still "pending" we show nothing — so when
  // Intercom IS available the launcher takes over with no flash of the fallback.
  const [showSupportFallback, setShowSupportFallback] = useState(
    () => getAnonIntercomStatus() === 'unavailable'
  );

  useEffect(() => {
    const applyStatus = (status) => setShowSupportFallback(status === 'unavailable');

    applyStatus(getAnonIntercomStatus());
    const unsubscribe = subscribeAnonIntercomStatus(applyStatus);

    const graceTimerId = window.setTimeout(() => {
      if (getAnonIntercomStatus() !== 'active') {
        setShowSupportFallback(true);
      }
    }, SUPPORT_FALLBACK_GRACE_MS);

    return () => {
      unsubscribe();
      window.clearTimeout(graceTimerId);
    };
  }, []);

  // { type: "credentials" | "connection" | "membership" | "rate_limit", message, renewUrl } | null
  const [authError, setAuthError] = useState(null);
  const [forgotOpen, setForgotOpen] = useState(false);

  const completeLogin = async (values) => {
    setAuthError(null);
    const response = await login(values);

    if (response.status) {
      successMessage('Logged in successfully.');
      navigate('/main/dashboard');
      return;
    }

    const type = classifyLoginError(response);
    // FIXED copy — never the server's generic text. The two data-driven
    // exceptions: the renew target (server names the expired account's membership
    // site) and the rate-limit text (server message carries the live "try again
    // in Ns" countdown, which leaks nothing).
    setAuthError({
      type,
      message: type === 'rate_limit' ? response.message || ERROR_COPY.rate_limit : ERROR_COPY[type],
      renewUrl: response.renewUrl || response.membershipSite || response.domain || DEFAULT_RENEW_URL
    });
  };

  const fieldError = authError?.type === 'credentials';
  const fieldBorder = fieldError ? 'solid 1px #e2555f' : 'solid 1px #343847';

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          width: '100vw',
          height: '100vh',
          zIndex: 1,
          background: 'radial-gradient(40% 40% at 50% 40%, rgba(19, 33, 65, 1) 0%,  rgba(22, 23, 30, 1) 100%)'
        }}
      />
      <Stack
        alignItems="center"
        sx={{
          pt: { xs: 5, md: '100px' },
          maxWidth: 425,
          width: '100%',
          minHeight: '100vh',
          color: 'white',
          margin: 'auto',
          zIndex: 2,
          paddingX: { xs: 2.5, md: 6 }
        }}
      >
        <Box sx={{ mb: '60px' }}>
          <img src={LogoIcon} alt="logo" style={{ height: 24 }} />
        </Box>
        <Typography
          textAlign="center"
          sx={{
            fontSize: 24,
            fontWeight: 600,
            lineHeight: '30px',
            letterSpacing: -0.15,
            mb: 1
          }}
        >
          Log in to your account
        </Typography>
        <Typography textAlign="center" sx={{ fontSize: 14, lineHeight: '20px', color: '#aeb6c2', mb: 3 }}>
          Use the same login as your membership site.
        </Typography>
        <Formik
          initialValues={{
            log: '',
            pwd: ''
          }}
          validationSchema={Yup.object().shape({
            log: Yup.string().max(255).required('Username is required'),
            pwd: Yup.string().max(255).required('Password is required')
          })}
          onSubmit={async (values, { setSubmitting }) => {
            setSubmitting(true);
            await completeLogin(values);
            setSubmitting(false);
          }}
        >
          {({ handleBlur, handleChange, handleSubmit, errors, isSubmitting, touched, values }) => (
            <form noValidate onSubmit={handleSubmit} style={{ width: '100%' }}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 12 }}>
                  <Stack spacing={1}>
                    <OutlinedInput
                      id="log-login"
                      type="text"
                      value={values.log}
                      name="log"
                      disabled={isSubmitting}
                      onBlur={handleBlur}
                      onChange={(event) => {
                        if (authError) setAuthError(null);
                        handleChange(event);
                      }}
                      placeholder="Email or username"
                      fullWidth
                      error={Boolean(touched.log && errors.log)}
                      size="small"
                      sx={{
                        color: 'white',
                        bgcolor: '#252731',
                        border: fieldBorder
                      }}
                    />
                    {touched.log && errors.log && (
                      <FormHelperText error id="standard-weight-helper-text-log-login" sx={{ fontSize: '14px' }}>
                        {errors.log}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Stack spacing={1}>
                    <OutlinedInput
                      fullWidth
                      placeholder="Password"
                      color={capsWarning ? 'warning' : 'primary'}
                      error={Boolean(touched.pwd && errors.pwd)}
                      id="pwd-login"
                      type={showPassword ? 'text' : 'password'}
                      value={values.pwd}
                      name="pwd"
                      size="small"
                      disabled={isSubmitting}
                      sx={{
                        color: 'white',
                        bgcolor: '#252731',
                        border: fieldBorder
                      }}
                      onBlur={(event) => {
                        setCapsWarning(false);
                        handleBlur(event);
                      }}
                      onKeyDown={(keyEvent) => {
                        if (keyEvent.getModifierState('CapsLock')) {
                          setCapsWarning(true);
                        } else {
                          setCapsWarning(false);
                        }
                      }}
                      onChange={(event) => {
                        if (authError) setAuthError(null);
                        handleChange(event);
                      }}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword((prev) => !prev)}
                            onMouseDown={(e) => e.preventDefault()}
                            edge="end"
                          >
                            {showPassword ? (
                              <VisibilityOutlined sx={{ color: 'white' }} />
                            ) : (
                              <VisibilityOffOutlined sx={{ color: 'white' }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                    {capsWarning && (
                      <Typography variant="caption" sx={{ color: 'warning.main' }} id="warning-helper-text-password-login">
                        Caps lock on!
                      </Typography>
                    )}
                    {touched.pwd && errors.pwd && (
                      <FormHelperText error id="standard-weight-helper-text-pwd-login" sx={{ fontSize: '14px' }}>
                        {errors.pwd}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Stack direction="row" justifyContent="flex-end">
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={() => setForgotOpen(true)}
                      sx={{ fontSize: 13, color: '#7c8cf8', '&:hover': { color: '#9aa6ff' } }}
                    >
                      Forgot password?
                    </Link>
                  </Stack>
                </Grid>

                {(authError?.type === 'credentials' ||
                  authError?.type === 'connection' ||
                  authError?.type === 'rate_limit') && (
                  <Grid size={{ xs: 12 }}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: authError.type === 'rate_limit' ? '#f0c25a' : '#ff7a7a',
                        lineHeight: '18px'
                      }}
                    >
                      {authError.message}
                    </Typography>
                  </Grid>
                )}

                {authError?.type === 'membership' && (
                  <Grid size={{ xs: 12 }}>
                    <Box
                      sx={{
                        p: 1.25,
                        borderRadius: '8px',
                        backgroundColor: 'rgba(229, 169, 60, 0.12)',
                        border: '1px solid rgba(229, 169, 60, 0.30)'
                      }}
                    >
                      <Typography sx={{ fontSize: 13, color: '#f3d9a6', lineHeight: '18px' }}>{authError.message}</Typography>
                      <Box
                        component="span"
                        role="link"
                        tabIndex={0}
                        onClick={() => openExternal(authError.renewUrl)}
                        onKeyDown={(keyEvent) => {
                          if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                            keyEvent.preventDefault();
                            openExternal(authError.renewUrl);
                          }
                        }}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          mt: 0.5,
                          fontSize: 13,
                          fontWeight: 500,
                          color: '#7c8cf8',
                          cursor: 'pointer',
                          '&:hover': { color: '#9aa6ff' }
                        }}
                      >
                        Renew it on your membership site
                        <OpenInNewRoundedIcon sx={{ fontSize: 13, ml: 0.5 }} />
                      </Box>
                    </Box>
                  </Grid>
                )}

                <Grid size={{ xs: 12 }}>
                  <Button
                    disableElevation
                    disabled={isSubmitting}
                    fullWidth
                    type="submit"
                    variant="contained"
                    size="large"
                    sx={{ mt: 2 }}
                  >
                    {isSubmitting ? 'Logging in…' : 'Log In'}
                  </Button>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Typography textAlign="center" sx={{ fontSize: 13, color: '#7c8597', mt: 1.5 }}>
                    Don&apos;t have an account? Get access on your membership site.
                  </Typography>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </Stack>

      {showSupportFallback && (
        <Tooltip title="Help & support" placement="left">
          <IconButton
            aria-label="Help and support"
            onClick={() => openExternal(SUPPORT_URL)}
            sx={{
              position: 'fixed',
              right: 24,
              bottom: 24,
              zIndex: 3,
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: '#232734',
              color: '#c7cdd9',
              border: '1px solid #343951',
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              '&:hover': { backgroundColor: '#2b3040' }
            }}
          >
            <ChatBubbleOutlineRoundedIcon />
          </IconButton>
        </Tooltip>
      )}

      <ForgotPasswordModal open={forgotOpen} onClose={() => setForgotOpen(false)} onSubmit={forgotPassword} defaultEmail="" />
    </>
  );
};

export default Login;
